import { useTranslation } from 'react-i18next';
import { InstallApp } from '@/components/InstallApp';
import { LogOut, Languages, Search, Check } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { TajerGoBrand } from '@/components/brand/TajerGoBrand';
import { useLogout } from './useLogout';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { CommandPalette } from '@/components/ui/CommandPalette';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { SupportAvailabilityToggle } from '@/components/layout/SupportAvailabilityToggle';
import { useAuthStore } from '@/store/authStore';
import { getDashboardHealthSnapshot } from '@/api/health.api';
import { cn } from '@/lib/cn';
import { SUPPORTED_LANGUAGES, normalizeLanguage, supportedLanguageCodes } from '@/i18n/languageConfig';


type HealthStatus = 'healthy' | 'degraded' | 'unhealthy' | 'unknown';

/**
 * Health status pill in the top bar.
 *
 * - Polls /api/health* every 60s in the background.
 * - Renders a coloured dot only - clicking expands a small popover with
 *   per-subsystem detail.
 * - DOES NOT block any other content from rendering. A failed health
 *   check just shows a red dot; the rest of the app continues normally.
 */
export function HealthPill({ inline = false }: { inline?: boolean } = {}): JSX.Element {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const { data, isError } = useQuery({
        queryKey: ['platform-topbar-health'],
        queryFn: getDashboardHealthSnapshot,
        refetchInterval: 60_000,
        retry: 1,
    });

    const status: HealthStatus = (() => {
        if (isError) return 'unhealthy';
        if (!data) return 'unknown';
        const subs = [
            data.ping.status === 'ok',
            data.core.status === 'healthy',
            data.database.status === 'healthy',
            data.supabase.status === 'healthy',
            data.llm.status === 'healthy',
            data.infrastructure.configured && !data.infrastructure.error,
        ];
        if (subs.every(Boolean)) return 'healthy';
        if (subs.filter(Boolean).length >= 2) return 'degraded';
        return 'unhealthy';
    })();

    const dotClass =
        status === 'healthy'
            ? 'bg-tg-teal'
            : status === 'degraded'
              ? 'bg-tg-warning'
              : status === 'unhealthy'
                ? 'bg-tg-danger'
                : 'bg-tg-border-strong';

    const label =
        status === 'healthy'
            ? t('health.status.healthy')
            : status === 'degraded'
              ? t('health.status.degraded')
              : status === 'unhealthy'
                ? t('health.status.unhealthy')
                : t('health.status.unknown');

    return (
        <div className={inline ? 'w-full' : 'relative'}>
            <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                className="inline-flex h-11 min-w-11 items-center justify-center gap-2 rounded-full border sm:h-9 border-tg-border bg-white px-3 text-xs font-semibold text-tg-ink shadow-[var(--tg-shadow-card)] hover:bg-tg-surface-2"
                aria-label={t('health.platformHealthWithStatus', { status: label })}
            >
                <span
                    className={`h-2 w-2 rounded-full ${dotClass} ${status === 'healthy' ? '' : 'animate-pulse'}`}
                />
                <span className={inline ? 'inline' : 'hidden lg:inline'}>{label}</span>
            </button>
            {open && (
                <div
                    role="dialog"
                    aria-label={t('health.platformHealth')}
                    className={
                        inline
                            ? 'mt-2 w-full rounded-xl border border-tg-border bg-tg-surface-2 p-3 text-xs'
                            : 'absolute end-0 top-full z-50 mt-2 max-h-[calc(100dvh-6rem)] w-72 max-w-[calc(100vw-1.5rem)] overflow-y-auto rounded-2xl border border-tg-border bg-white p-3 text-xs shadow-[var(--tg-shadow-pop)]'
                    }
                >
                    <div className="font-semibold text-tg-ink">{t('health.platformHealth')}</div>
                    {data ? (
                        <ul className="mt-2 space-y-1.5 text-tg-muted">
                            <li className="flex items-center justify-between">
                                <span>{t('health.platformApi')}</span>
                                <Badge variant={data.ping.status === 'ok' ? 'success' : 'danger'}>
                                    {data.ping.status}
                                </Badge>
                            </li>
                            <li className="flex items-center justify-between">
                                <span>{t('health.coreApi')}</span>
                                <Badge variant={data.core.status === 'healthy' ? 'success' : 'danger'}>
                                    {data.core.status}
                                </Badge>
                            </li>
                            <li className="flex items-center justify-between">
                                <span>{t('health.database')}</span>
                                <Badge variant={data.database.status === 'healthy' ? 'success' : 'danger'}>
                                    {data.database.status}
                                </Badge>
                            </li>
                            <li className="flex items-center justify-between">
                                <span>{t('health.supabase')}</span>
                                <Badge variant={data.supabase.status === 'healthy' ? 'success' : 'danger'}>
                                    {data.supabase.status}
                                </Badge>
                            </li>
                            <li className="flex items-center justify-between">
                                <span>{t('health.llm')}</span>
                                <Badge variant={data.llm.status === 'healthy' ? 'success' : 'danger'}>
                                    {data.llm.status}
                                </Badge>
                            </li>
                            <li className="flex items-center justify-between">
                                <span>{t('health.infrastructure')}</span>
                                <Badge
                                    variant={
                                        data.infrastructure.configured && !data.infrastructure.error
                                            ? 'success'
                                            : 'danger'
                                    }
                                >
                                    {data.infrastructure.configured && !data.infrastructure.error
                                        ? t('health.status.healthy')
                                        : t('health.status.unhealthy')}
                                </Badge>
                            </li>
                        </ul>
                    ) : isError ? (
                        <p className="mt-2 text-tg-danger">
                            {t('health.endpointsUnavailable')}
                        </p>
                    ) : (
                        <p className="mt-2 text-tg-muted">{t('health.status.unknown')}</p>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * Language picker dropdown.
 *
 * Replaces the previous blind en→ar→es cycle button with an explicit
 * menu of native language labels, current selection checked. Changing
 * the language keeps i18next's RTL/dir switching behaviour (driven by
 * the languageChanged handler wired in i18n.ts).
 */
function LanguageMenu(): JSX.Element {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const current = normalizeLanguage(i18n.language);

    useEffect(() => {
        if (!open) return;
        const onDoc = (ev: MouseEvent): void => {
            if (containerRef.current && !containerRef.current.contains(ev.target as Node)) setOpen(false);
        };
        const onKey = (ev: KeyboardEvent): void => {
            if (ev.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const selectLanguage = async (code: string): Promise<void> => {
        setOpen(false);
        if (code !== current) await i18n.changeLanguage(code);
    };

    return (
        <div ref={containerRef} className="relative">
            <Button
                variant="ghost"
                onClick={() => setOpen((v) => !v)}
                aria-label={t('nav.selectLanguage', 'Select language')}
                aria-haspopup="menu"
                aria-expanded={open}
                title={SUPPORTED_LANGUAGES[current].nativeLabel}
                className="h-11 w-full justify-start gap-2 px-3"
            >
                <Languages className="h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate text-start text-sm">{SUPPORTED_LANGUAGES[current].nativeLabel}</span>
            </Button>
            {open && (
                <div
                    role="menu"
                    className="absolute end-0 top-full z-50 mt-2 max-h-[min(60dvh,20rem)] w-44 max-w-[calc(100vw-1.5rem)] overflow-y-auto rounded-2xl border border-tg-border bg-white py-1 shadow-[var(--tg-shadow-pop)]"
                >
                    {supportedLanguageCodes.map((code) => {
                        const isActive = code === current;
                        return (
                            <button
                                key={code}
                                type="button"
                                role="menuitemradio"
                                aria-checked={isActive}
                                onClick={() => void selectLanguage(code)}
                                className={cn(
                                    'flex min-h-11 w-full items-center justify-between gap-2 px-3 py-2 text-start text-sm font-medium transition-colors hover:bg-tg-page',
                                    isActive ? 'text-tg-ink' : 'text-tg-muted',
                                )}
                            >
                                <span className="truncate">{SUPPORTED_LANGUAGES[code].nativeLabel}</span>
                                {isActive && <Check className="h-4 w-4 shrink-0 text-tg-teal" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export function TopBar(): JSX.Element {
    const { t } = useTranslation();
    const user = useAuthStore((s) => s.user);
    const [paletteOpen, setPaletteOpen] = useState(false);

    // Global Ctrl/Cmd-K opens the command palette.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                setPaletteOpen((open) => !open);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    const logout = useLogout();

    return (
        <>
        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
        <header className="sticky top-0 z-20 flex h-[calc(var(--pa-app-bar-h)+var(--pa-safe-top))] shrink-0 items-center justify-between gap-2 border-b border-tg-border bg-white px-2 pt-[var(--pa-safe-top)] shadow-[0_1px_2px_rgba(15,42,68,0.04)] sm:gap-3 sm:px-3 md:px-6">
            <div className="flex min-w-0 items-center gap-3">
                {/* Phone: brand mark; navigation lives in the bottom tab bar (PhoneNav). */}
                <NavLink to="/dashboard" className="shrink-0 md:hidden" aria-label={t('nav.dashboard', 'Dashboard')}>
                    <TajerGoBrand product="Platform" tone="light" size="md" iconOnly />
                </NavLink>
                <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-tg-ink" title={user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email : undefined}>
                        {user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email : ''}
                    </div>
                    <div className="hidden text-xs font-medium text-slate-500 sm:block">
                        {t('app.operatorWorkspace')}
                    </div>
                </div>
            </div>
            <div className="hidden min-w-0 flex-1 justify-center xl:flex">
                <button
                    type="button"
                    onClick={() => setPaletteOpen(true)}
                    className="flex h-10 w-full max-w-[420px] items-center gap-2 rounded-[11px] border border-tg-border bg-tg-surface-2 px-3 text-tg-subtle transition-colors hover:border-tg-teal hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tg-teal/40"
                    aria-label={t('nav.searchConsole', 'Search console')}
                    aria-haspopup="dialog"
                >
                    <Search className="h-4 w-4 shrink-0" />
                    <span className="min-w-0 flex-1 truncate text-start text-sm font-medium">
                        {t('nav.searchConsole', 'Search console')}
                    </span>
                    <kbd className="hidden shrink-0 rounded border border-tg-border bg-white px-1.5 py-0.5 text-[0.65rem] font-semibold text-tg-subtle sm:inline">
                        {'⌘K'}
                    </kbd>
                </button>
            </div>
            <div className="flex shrink-0 items-center gap-1 sm:gap-2">
                {/* Below xl the search field collapses to an icon so it stays reachable. */}
                <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setPaletteOpen(true)}
                    aria-label={t('nav.searchConsole', 'Search console')}
                    aria-haspopup="dialog"
                    className="h-11 w-11 xl:hidden"
                >
                    <Search className="h-4 w-4" />
                </Button>
                {user?.role && (
                    <Badge variant="secondary" className="hidden max-w-40 truncate px-3 py-1 text-[0.7rem] font-bold tracking-wide md:inline-flex">
                        {user.role}
                    </Badge>
                )}
                <div className="hidden md:block">
                    <HealthPill />
                </div>
                <div className="hidden md:block">
                    <SupportAvailabilityToggle />
                </div>
                <NotificationBell />
                {/* md+ only: on phones these actions live in the More sheet. */}
                <details
                    className="relative hidden md:block"
                    onKeyDown={(event) => {
                        if (event.key === 'Escape') event.currentTarget.removeAttribute('open');
                    }}
                >
                    <summary aria-label={t('appInstall.accountMenu')} className="flex h-11 w-11 cursor-pointer list-none items-center justify-center rounded-full bg-tg-teal/15 text-sm font-bold text-tg-ink focus-visible:outline focus-visible:outline-2 [&::-webkit-details-marker]:hidden">
                        {user?.firstName?.[0] || user?.email?.[0] || 'TG'}
                    </summary>
                    <div className="absolute end-0 z-50 mt-2 max-h-[calc(100dvh-5rem)] w-64 max-w-[calc(100vw-1rem)] space-y-2 overflow-y-auto rounded-xl border border-tg-border bg-white p-3 shadow-lg">
                        <p className="truncate text-sm font-semibold" title={user?.email}>{user?.email}</p>
                        <LanguageMenu />
                        <InstallApp />
                        <Button size="sm" variant="outline" onClick={logout} className="h-10 w-full px-3">
                            <LogOut className="h-4 w-4" />
                            <span>{t('nav.logout')}</span>
                        </Button>
                    </div>
                </details>
            </div>
        </header>
        </>
    );
}
